import React, { Fragment } from 'react';
import moment from 'moment-timezone';
import { I18n, Translate } from 'react-redux-i18n';

import AnalyticsServer from '../../../app/server/analytics';
import { AnalyticsPanel } from './AnalyticsVisuals';
import {
  defaultAnalyticsDateRange,
  formatAnalyticsApiDate,
} from '../utils/dateRange';

const TZ = 'America/Santiago';

const EVENT_LABELS = {
  login: 'analyticsLogin',
  sign_up: 'analyticsSignUp',
  view_item: 'analyticsViewItem',
  add_to_cart: 'analyticsAddToCart',
  remove_from_cart: 'analyticsRemoveFromCart',
  add_to_wishlist: 'analyticsAddToWishlist',
  begin_checkout: 'analyticsBeginCheckout',
  add_shipping_info: 'analyticsAddShippingInfo',
  add_payment_info: 'analyticsAddPaymentInfo',
  purchase: 'analyticsPurchases',
  generate_lead: 'analyticsGenerateLead',
  search: 'analyticsSearch',
  contact_us_click: 'analyticsContactClick',
};

function defaultDateRange() {
  return defaultAnalyticsDateRange(6);
}

function formatApiDate(date) {
  return formatAnalyticsApiDate(date);
}

function eventLabel(eventName) {
  const key = EVENT_LABELS[eventName];
  return key ? I18n.t(key) : eventName.replace(/_/g, ' ');
}

function formatEventDetail(event) {
  const { payload = {} } = event;
  const parts = [];

  if (payload.search_term) parts.push(`${I18n.t('analyticsSearchTerm')}: ${payload.search_term}`);
  if (payload.transaction_id) parts.push(`${I18n.t('analyticsTransactionId')}: ${payload.transaction_id}`);
  if (payload.value != null) parts.push(`${I18n.t('analyticsValue')}: ${payload.value}`);
  if (payload.lead_type) parts.push(`${I18n.t('analyticsLeadType')}: ${payload.lead_type}`);

  return parts.join(' · ');
}

class JourneyTab extends React.PureComponent {
  constructor(props) {
    super(props);
    const { startDate, endDate } = defaultDateRange();
    this.state = {
      query: '',
      searching: false,
      hasSearched: false,
      loadingTimeline: false,
      searchResults: [],
      selectedUser: null,
      timeline: null,
      startDate,
      endDate,
      errorMessage: '',
    };
  }

  onSearch = async (e) => {
    e.preventDefault();
    const { query } = this.state;
    if ((query || '').trim().length < 2) return;

    this.setState({
      searching: true,
      errorMessage: '',
      searchResults: [],
      selectedUser: null,
      timeline: null,
      hasSearched: true,
    });

    try {
      const data = await AnalyticsServer.searchJourneyUsers(query.trim());
      this.setState({
        searching: false,
        searchResults: (data && data.rows) || [],
      });
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        searching: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  selectUser = (user) => {
    this.setState({ selectedUser: user }, () => {
      this.loadTimeline(user);
    });
  };

  loadTimeline = async (user) => {
    const { startDate, endDate } = this.state;
    const start = formatApiDate(startDate);
    const end = formatApiDate(endDate);

    this.setState({ loadingTimeline: true, errorMessage: '' });

    try {
      const params = user.userId
        ? { userId: user.userId, startDate: start, endDate: end }
        : { userEmail: user.userEmail, startDate: start, endDate: end };
      const data = await AnalyticsServer.getJourneyTimeline(params);
      this.setState({
        loadingTimeline: false,
        timeline: data,
      });
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loadingTimeline: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  renderIntro = () => (
    <div className="analytics-journey-intro" role="note">
      <p>
        <Translate value="analyticsJourneyIntro" />
      </p>
    </div>
  );

  renderSearchResults = () => {
    const { searchResults, hasSearched, searching } = this.state;

    if (!hasSearched || searching) {
      return null;
    }

    if (!searchResults.length) {
      return (
        <div className="analytics-empty margin-top-20">
          <p>
            <Translate value="analyticsJourneyNoResults" />
          </p>
          <p>
            <Translate value="analyticsJourneyNoResultsHint" />
          </p>
        </div>
      );
    }

    return (
      <Fragment>
        <h4 className="analytics-journey-results-title">
          <Translate value="analyticsJourneyResultsTitle" />
        </h4>
        <ul className="analytics-journey-users">
          {searchResults.map((row) => {
            const key = row.userId || row.userEmail;
            return (
              <li key={key} className="analytics-journey-users__item">
                <button
                  type="button"
                  className="analytics-journey-users__btn"
                  onClick={() => this.selectUser(row)}
                >
                  <strong>{row.userName || row.userEmail}</strong>
                  {row.userEmail && row.userName && (
                    <span className="analytics-journey-users__meta">{row.userEmail}</span>
                  )}
                  {row.clientName && (
                    <span className="analytics-journey-users__meta">{row.clientName}</span>
                  )}
                  <span className="analytics-journey-users__meta">
                    <Translate value="analyticsJourneyLastAction" />
                    {': '}
                    {eventLabel(row.lastEventName)}
                    {' · '}
                    {moment(row.lastEventAt).tz(TZ).format('L LT')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Fragment>
    );
  };

  renderTimeline = () => {
    const { timeline, loadingTimeline, selectedUser, hasSearched } = this.state;

    if (!hasSearched) {
      return (
        <p className="analytics-journey-hint margin-top-20">
          <Translate value="analyticsJourneySelectHint" />
        </p>
      );
    }

    if (!selectedUser) return null;

    if (loadingTimeline) {
      return (
        <p className="margin-top-20">
          <Translate value="analyticsLoading" />
        </p>
      );
    }

    if (!timeline || !timeline.events || !timeline.events.length) {
      return (
        <div className="analytics-empty margin-top-20">
          <p>
            <Translate value="analyticsJourneyEmpty" />
          </p>
          <p>
            <Translate value="analyticsJourneyEmptyHint" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsJourneyTimeline">
        <p className="analytics-journey-hint">
          <Translate value="analyticsJourneyTimelineSubtitle" />
        </p>
        <p className="margin-bottom-15" style={{ color: '#2d353c', fontWeight: 600 }}>
          {selectedUser.userName || selectedUser.userEmail}
          {selectedUser.clientName && ` · ${selectedUser.clientName}`}
        </p>
        <ol className="analytics-journey-timeline">
          {timeline.events.map((event) => {
            const detail = formatEventDetail(event);
            return (
              <li key={event.id} className={`analytics-journey-timeline__item analytics-journey-timeline__item--${event.eventName}`}>
                <div className="analytics-journey-timeline__dot" aria-hidden="true" />
                <div className="analytics-journey-timeline__body">
                  <p className="analytics-journey-timeline__title">
                    {eventLabel(event.eventName)}
                    <span className="analytics-journey-timeline__time">
                      {moment(event.eventAt).tz(TZ).format('L LT')}
                    </span>
                  </p>
                  {event.pagePath && (
                    <p className="analytics-journey-timeline__path">
                      <Translate value="analyticsJourneyPage" />
                      {': '}
                      {event.pagePath}
                    </p>
                  )}
                  {detail && (
                    <p className="analytics-journey-timeline__detail">{detail}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </AnalyticsPanel>
    );
  };

  render() {
    const { query, searching, errorMessage } = this.state;

    return (
      <div className="analytics-page margin-top-20">
        {this.renderIntro()}

        <AnalyticsPanel titleKey="analyticsJourneySearch">
          <p className="analytics-journey-hint">
            <Translate value="analyticsJourneySearchHint" />
          </p>
          <form onSubmit={this.onSearch} className="analytics-journey-search">
            <div className="row">
              <div className="col-lg-8">
                <input
                  type="search"
                  className="form-control"
                  placeholder={I18n.t('analyticsJourneySearchPlaceholder')}
                  value={query}
                  onChange={(e) => this.setState({ query: e.target.value })}
                  aria-label={I18n.t('analyticsJourneySearch')}
                />
              </div>
              <div className="col-lg-4 margin-top-10 margin-top-lg-0">
                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  disabled={searching || (query || '').trim().length < 2}
                >
                  {searching ? I18n.t('analyticsLoading') : I18n.t('search')}
                </button>
              </div>
            </div>
          </form>
        </AnalyticsPanel>

        {errorMessage && (
          <p className="color-red margin-top-20">{errorMessage}</p>
        )}

        {this.renderSearchResults()}
        {this.renderTimeline()}
      </div>
    );
  }
}

export default JourneyTab;
